import clsx from 'clsx';
import React from 'react';
import { useDispatch } from 'react-redux';

import AnchorLink from '@/src/components/common/Elements/AnchorLink/AnchorLink';
import Button from '@/src/components/common/Elements/Button/Button';
import TileCard from '@/src/components/common/Elements/Card/TileCard/TileCard';
import Icon, { ICONS, ICON_TYPES } from '@/src/components/common/Elements/Icon/Icon';
import Link from '@/src/components/common/Elements/Link/Link';
import LongTextEllipsisInBetween from '@/src/components/common/Elements/LongText/LongTextEllipsisInBetween';
import StatusIndicator from '@/src/components/project/Common/StatusIndicator/StatusIndicator';
import { TaskStatusEnum } from '@/src/components/task/Config';
import { SubtaskTypeEnum } from '@/src/components/task/Details/Tabs/Subtasks/Config';
import { getFilePathExtension } from '@/src/globalFunction';
import { initialize as initializeTaskPanel } from '@/src/reducers/tasks/taskForm/taskForm.actions';
import { useBreakpoint } from '@/src/hooks/useBreakpoint';

import WorkstreamAvatar from '../../../../Elements/WorkstreamAvatar/WorkstreamAvatar';
import { SubtaskReport } from '../../types';

function getDocumentIcon(ext: string | null, completed: boolean) {
  const icon = ICONS.file || (ext && ICONS[ext]);
  return (
    <Icon
      icon={icon}
      className={clsx(
        'tw-mr-[7px] tw-text-[20px]',
        completed ? 'tw-text-cfo-blue-600' : 'tw-text-cfo-grey-600 tw-opacity-[0.50]'
      )}
    />
  );
}

function getLinkIcon(completed: boolean, hasHref: boolean) {
  return (
    <Icon
      icon={ICONS.link}
      className={clsx(
        'tw-rotate-[-50deg] tw-mr-[7px] tw-text-[24px]',
        completed
          ? 'tw-text-cfo-blue-600'
          : hasHref
            ? 'tw-text-cfo-blue-600 tw-opacity-[0.50]'
            : 'tw-text-cfo-grey-600'
      )}
    />
  );
}

function getLabel(subtaskReport: SubtaskReport, completed: boolean, isLargeDevice: boolean) {
  const icon =
    subtaskReport.type === SubtaskTypeEnum.DOCUMENT
      ? getDocumentIcon(
          subtaskReport.rawName ? getFilePathExtension(subtaskReport.rawName).toLowerCase() : null,
          completed
        )
      : getLinkIcon(completed, !!subtaskReport.href);

  const labelContent = (
    <div
      className={clsx('tw-flex tw-items-center', 'tw-min-h-[35px]', 'tw-h-auto', 'tw-flex-wrap')}
      style={isLargeDevice ? { justifyContent: 'center', textAlign: 'center' } : undefined}
    >
      {icon}
      <LongTextEllipsisInBetween
        value={subtaskReport.label}
        id={subtaskReport.id}
        maxLength={50}
        firstCharactersLength={25}
        lastCharactersLength={22}
      />
    </div>
  );

  if (!completed) return labelContent;

  if (subtaskReport.type === SubtaskTypeEnum.DOCUMENT) {
    return (
      <Link to={subtaskReport.href} target="_blank">
        {labelContent}
      </Link>
    );
  }
  return (
    <AnchorLink href={subtaskReport.href} target="_blank">
      {labelContent}
    </AnchorLink>
  );
}

function getAdjustedDateColor(completed: boolean, isDelayed: boolean) {
  if (isDelayed && !completed) return 'tw-text-cfo-red-400';
  return 'tw-text-cfo-black';
}

export type SubtaskReportVerticalItemProps = {
  subtaskReport: SubtaskReport;
};

function SubtaskReportVerticalItem({ subtaskReport }: SubtaskReportVerticalItemProps) {
  const dispatch = useDispatch();
  const completed = subtaskReport.status === TaskStatusEnum.Completed;
  const device = useBreakpoint();
  const isLargeDevice =
    device.breakpoint === 'lg' || device.breakpoint === 'md' || device.breakpoint === 'sm';
  const label = getLabel(subtaskReport, completed, isLargeDevice);

  async function handleOpenTask() {
    dispatch({ type: 'SET_SCREEN_LOADER', Loading: true });
    await dispatch(initializeTaskPanel(subtaskReport.taskId));
    $(`#task-details`).modal('show');
    dispatch({ type: 'SET_SCREEN_LOADER', Loading: false });
  }

  return (
    <TileCard
      className={clsx(
        'tw-w-full',
        'tw-border-cfo-grey-500',
        'tw-shadow-none',
        'tw-p-2',
        'tw-box-border'
      )}
    >
      <div
        className={clsx(
          'tw-flex',
          'tw-flex-row',
          isLargeDevice && 'tw-flex-col',
          isLargeDevice && 'tw-justify-center',
          'tw-items-stretch',
          'tw-w-full',
          'tw-min-w-0',
          'tw-gap-[12px',
          'lg:tw-gap-[16px]'
        )}
      >
        {/* Label/Link always on top, wraps naturally */}
        <div
          className={clsx(
            'tw-w-full',
            'tw-break-words',
            'tw-min-w-0',
            'tw-flex',
            'tw-items-center',
            'tw-mb-1', // always a small gap below label
            'lg:tw-mb-0',
            isLargeDevice && 'tw-justify-center'
          )}
        >
          {label}
        </div>
        {/* Right: Date, Avatar, Button, Status, wraps below label if needed */}
        <div
          className={clsx(
            'tw-flex',
            'tw-flex-row',
            'tw-flex-wrap',
            'tw-items-center',
            'tw-justify-end',
            'tw-gap-y-1',
            'tw-min-w-0',
            'tw-w-full',
            'lg:tw-w-auto',
            isLargeDevice && 'tw-justify-center',
            'tw-mt-1',
            'lg:tw-mt-0'
          )}
        >
          <div className={clsx('tw-flex', 'tw-flex-row', 'tw-items-center', 'tw-flex-nowrap')}>
            <WorkstreamAvatar
              id={subtaskReport.id}
              name={subtaskReport.workstreamName}
              avatar={'default.png'}
              workstreamColor={subtaskReport.workstreamColor}
            />
            <span
              className={clsx(
                'tw-text-[10px]',
                'tw-ml-2',
                'tw-whitespace-nowrap',
                getAdjustedDateColor(completed, subtaskReport.isDelayed)
              )}
            >
              {subtaskReport.adjustedDate}
            </span>
            <Button
              data-tooltip-id={`sti_${subtaskReport.id}_tooltip`}
              onClick={handleOpenTask}
              className={clsx('tw-leading-none', 'tw-ml-2', 'tw-px-2', 'tw-py-1')}
              tooltip={{ content: 'Go to task' }}
            >
              <Icon
                icon={ICONS.arrow_right}
                className={clsx('tw-text-[18px]', 'tw-text-cfo-black')}
              />
            </Button>
          </div>
          <StatusIndicator
            status={subtaskReport.status}
            className={clsx(
              'tw-flex',
              'tw-items-center',
              'tw-justify-center',
              '!tw-mt-0',
              '!tw-mb-0',
              'tw-ml-2'
            )}
          >
            {completed && (
              <Icon
                className={clsx('tw-mr-[3px]', 'tw-text-[14px]')}
                icon={'check_circle'}
                iconType={ICON_TYPES.rounded}
              />
            )}
          </StatusIndicator>
        </div>
      </div>
    </TileCard>
  );
}

export default SubtaskReportVerticalItem;
